// Cuanto vale cada cargo cuando su jugador tiene una promocion, y que hacer con
// lo que ya se cobro.
//
// Antes esto vivia en tres lugares que hacian tres cosas distintas: la emision
// del plan (calculaba el descuento al generar y nunca despues), Estado de cuenta
// (rebajaba cargos existentes, pero ignoraba a los hermanos y a la temporada) y
// el recalculo de la promocion (solo tocaba cargos que YA tuvieran promocion_id).
// El resultado medido: promociones activas que no le descontaban un peso a nadie.
//
// La regla es una sola, y es la de cualquier sistema de facturacion:
//
//   hacia adelante se descuenta, hacia atras se acredita.
//
// Un cargo que no ha recibido dinero es borrador: se le baja el precio y el papa
// ve la linea del descuento. Uno que ya recibio dinero es historia: no se toca y
// el beneficio le llega como saldo a favor.
//
// Puro: recibe lo ya consultado y no toca la red. Quien consulta y escribe es el
// motor, en promocionRecalculo.js.
import { calcularDescuento } from './descuentoPromociones'

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100

// OJO con `pagadoRealPorCargo`: es lo pagado con DINERO, no `monto_pagado`.
// Aplicar un saldo a favor sube monto_pagado, pero es credito del propio club --
// si contara como pago, una beca aplicada dejaria al jugador fuera del descuento
// de hermanos y el club se bloquearia solo.
export const calcularCambiosPorPromocion = ({
  cargos = [], promociones = [], jugadorId, jugadorFamilia = {}, jugadorPosicion = {},
  pagadoRealPorCargo = {}, creditosExistentes = [],
} = {}) => {
  const cambios = []
  const creditos = []
  const historia = []
  // Cuanto se le ha acreditado YA por cada (promocion, cargo). Se resta, no se usa
  // como sí/no: asi el primer credito se otorga entero, subir el descuento otorga
  // solo la diferencia, y correr el motor de nuevo no otorga nada.
  const acreditado = {}
  ;(creditosExistentes || []).forEach((c) => {
    const k = `${c.promocion_id}|${c.cargo_origen_id}`
    acreditado[k] = round2((acreditado[k] || 0) + Number(c.monto || 0))
  })

  for (const c of (cargos || [])) {
    const { descuento, promocionId } = calcularDescuento({
      promociones, jugadorId, jugadorFamilia, jugadorPosicion,
      parc: { concepto_id: c.concepto_id }, planId: c.plan_id,
    })
    const original = Number(c.monto_original || 0)
    const descMonto = round2(original * (descuento / 100))
    const nuevoFinal = round2(original - descMonto)
    const pagado = Number(c.monto_pagado || 0)
    const pagadoReal = Number(pagadoRealPorCargo[c.id] || 0)

    if (pagadoReal > 0) {
      historia.push({ id: c.id, antes: Number(c.monto_final || 0), monto_pagado: pagado })
      // Se acredita SOLO lo que le falta, no el descuento entero: si el cargo ya
      // trae parte de la rebaja aplicada -- porque se emitio con ella -- darle el
      // importe completo seria descontarle dos veces. Visto en QA: un cargo de
      // $1,500 emitido en $1,350 recibia ademas $150 de saldo.
      const faltante = round2(
        descMonto - Number(c.descuento_monto || 0) - (acreditado[`${promocionId}|${c.id}`] || 0)
      )
      if (faltante > 0 && promocionId) {
        creditos.push({
          cargoId: c.id, jugadorId, promocionId, monto: faltante,
          temporadaId: c.temporada_id || null, motivo: 'historia',
        })
      }
      continue
    }

    const sinCambio = round2(nuevoFinal - Number(c.monto_final || 0)) === 0
      && Number(c.descuento_porcentaje || 0) === descuento
    if (sinCambio) continue

    // El saldo es lo que FALTA, no el monto nuevo: ponerlo a secas borraria lo
    // ya abonado con saldo a favor.
    let saldo = round2(nuevoFinal - pagado)
    let excedente = 0
    if (saldo < 0) { excedente = round2(-saldo); saldo = 0 }
    let estatus = 'pendiente'
    if (saldo <= 0) estatus = 'pagado'
    else if (pagado > 0) estatus = 'parcial'

    cambios.push({
      id: c.id,
      antes: Number(c.monto_final || 0),
      despues: nuevoFinal,
      descuento_porcentaje: descuento,
      descuento_monto: descMonto,
      monto_final: nuevoFinal,
      saldo,
      estatus,
      promocion_id: promocionId,
      excedente,
    })

    if (excedente > 0) {
      creditos.push({
        cargoId: c.id, jugadorId, promocionId, monto: excedente,
        temporadaId: c.temporada_id || null, motivo: 'excedente',
      })
    }
  }

  return { cambios, creditos, historia }
}
